/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useQuery } from '@tanstack/react-query';
import { getClientInterests } from '@services/requests/housien-services/ClientInsightsService';
import { useClientDeals } from './useClientDeals';
import { useClientHistory } from './useClientHistory';
import { useClientOverview } from './useClientOverview';

export interface ClientPerformance {
  visits: number;
  proposals: number;
  interests: number;
  interactions: number;
  /** Whole days since the most recent history entry, or null when there is none. */
  daysSinceLastInteraction: number | null;
  /** Whole days since the client was registered, or null without a date. */
  daysInBase: number | null;
}

/**
 * Whole days between now and an ISO timestamp.
 *
 * @param iso - The reference timestamp, if any.
 * @returns The day count, or null when the timestamp is absent or unparseable.
 */
function daysSince(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return null;
  return Math.max(0, Math.floor((Date.now() - then) / 86_400_000));
}

/**
 * The six figures the client header strip reports.
 *
 * None of them arrives as a figure: visits come off the insights
 * summary, offers out of the client's attendances, interests off their
 * own listing, and the interaction count out of the paginated history.
 * They are gathered here so the strip reads six numbers rather than four
 * loading states.
 *
 * @param agencyId - The owning agency.
 * @param clientId - The client being shown.
 * @param registrationDate - When the client entered the base.
 * @returns The six figures, zeroed while their sources load.
 */
export function useClientPerformance(
  agencyId: string,
  clientId: string,
  registrationDate?: string,
): ClientPerformance {
  const { summary } = useClientOverview({ agencyId, clientId });
  const { deals } = useClientDeals(agencyId, clientId);
  const history = useClientHistory({ agencyId, clientId });
  const interests = useQuery({
    queryKey: ['clients', agencyId, 'interests', clientId] as const,
    queryFn: () => getClientInterests(agencyId, clientId),
    enabled: Boolean(agencyId && clientId),
  });

  return {
    visits: summary?.total_visits ?? 0,
    proposals: deals.reduce((total, deal) => total + (deal.proposals?.length ?? 0), 0),
    interests: interests.data?.length ?? 0,
    interactions: history.totalItems,
    daysSinceLastInteraction: daysSince(history.events[0]?.date),
    daysInBase: daysSince(registrationDate),
  };
}
